import React, { useCallback, useEffect, useRef, useState } from "react";
import { HiOutlineArrowDown } from "react-icons/hi";
import { VERTEX360_COPILOT_DRAWER_TRANSITION_MS } from "../constants/vertex360CopilotConstants";
import { formatChatTimestamp, shouldShowTimestamp } from "../utils/vertex360CopilotChatUtils";
import ChatMessage from "./ChatMessage";
import EmptyConversation from "./EmptyConversation";
import AgentTypingIndicator from "./AgentTypingIndicator";
import { Vertex360CopilotChatMessagesSkeleton } from "../workspace/Vertex360CopilotWorkspaceSkeleton";

const STICK_TO_BOTTOM_THRESHOLD_PX = 80;

const DateDivider = ({ label }) => (
  <div className="flex items-center gap-3 py-2">
    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
    <span className="shrink-0 rounded-full bg-white px-3 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 shadow-sm ring-1 ring-slate-200/80">
      {label}
    </span>
    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
  </div>
);

const ChatMessageList = ({
  messages,
  hasConversation,
  isLoadingMessages = false,
  conversationId,
  isAssistantTyping,
  isStreaming,
  streamingMessageId,
  isLoadingOlder,
  hasMoreOlder,
  onLoadOlder,
  onSelectPrompt,
  onRetryMessage,
  onClarifyOption,
  onMessageRated,
  scrollContainerRef,
  drawerVisible = false,
}) => {
  const topSentinelRef = useRef(null);
  const shouldStickToBottomRef = useRef(true);
  const hasUserScrolledUpRef = useRef(false);
  const pendingOpenScrollRef = useRef(false);
  const activeConversationIdRef = useRef(conversationId);
  const lastMessageKeyRef = useRef("");
  const scrollAttentionTimerRef = useRef(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [animateScrollToBottom, setAnimateScrollToBottom] = useState(false);

  const lastAssistantIndex = messages.reduce(
    (acc, msg, idx) => (msg.role === "assistant" ? idx : acc),
    -1
  );

  const streamingMessage = messages.find(
    (msg) => String(msg.id) === String(streamingMessageId ?? "")
  );
  const isWaitingForAssistantReply =
    isAssistantTyping ||
    (isStreaming && !String(streamingMessage?.content || "").trim());

  const scrollToBottom = useCallback(
    (behavior = "auto") => {
      const el = scrollContainerRef?.current;
      if (!el) return;

      const run = () => {
        const top = el.scrollHeight - el.clientHeight;
        if (behavior === "smooth" && typeof el.scrollTo === "function") {
          el.scrollTo({ top, behavior: "smooth" });
        } else {
          el.scrollTop = top;
        }
      };

      requestAnimationFrame(() => {
        requestAnimationFrame(run);
      });
    },
    [scrollContainerRef]
  );

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef?.current;
    if (!el) return;
    const distanceFromBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight;
    const isNearBottom = distanceFromBottom <= STICK_TO_BOTTOM_THRESHOLD_PX;
    shouldStickToBottomRef.current = isNearBottom;
    if (!isNearBottom) {
      hasUserScrolledUpRef.current = true;
    }
    setShowScrollToBottom(hasConversation && !isNearBottom);
  }, [hasConversation, scrollContainerRef]);

  useEffect(() => {
    const el = scrollContainerRef?.current;
    if (!el) return undefined;
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll, scrollContainerRef, hasConversation]);

  useEffect(() => {
    if (hasConversation || isLoadingMessages) return;

    const el = scrollContainerRef?.current;
    if (!el) return;

    shouldStickToBottomRef.current = true;
    setShowScrollToBottom(false);

    requestAnimationFrame(() => {
      el.scrollTop = 0;
    });
  }, [hasConversation, isLoadingMessages, scrollContainerRef]);

  useEffect(() => {
    if (scrollAttentionTimerRef.current) {
      window.clearTimeout(scrollAttentionTimerRef.current);
      scrollAttentionTimerRef.current = null;
    }

    if (!showScrollToBottom) {
      setAnimateScrollToBottom(false);
      return undefined;
    }

    setAnimateScrollToBottom(true);
    scrollAttentionTimerRef.current = window.setTimeout(() => {
      setAnimateScrollToBottom(false);
      scrollAttentionTimerRef.current = null;
    }, 5000);

    return () => {
      if (scrollAttentionTimerRef.current) {
        window.clearTimeout(scrollAttentionTimerRef.current);
        scrollAttentionTimerRef.current = null;
      }
    };
  }, [showScrollToBottom]);

  /** Opening or switching a persisted chat should land on the latest messages. */
  useEffect(() => {
    if (conversationId == null) return undefined;

    if (activeConversationIdRef.current !== conversationId) {
      activeConversationIdRef.current = conversationId;
      hasUserScrolledUpRef.current = false;
      lastMessageKeyRef.current = "";
      shouldStickToBottomRef.current = true;
      setShowScrollToBottom(false);
      pendingOpenScrollRef.current = true;
    }
  }, [conversationId]);

  useEffect(() => {
    if (!pendingOpenScrollRef.current || !hasConversation) return undefined;

    pendingOpenScrollRef.current = false;
    shouldStickToBottomRef.current = true;
    setShowScrollToBottom(false);
    scrollToBottom("auto");

    const afterLayout = window.setTimeout(() => scrollToBottom("auto"), 50);

    return () => window.clearTimeout(afterLayout);
  }, [conversationId, hasConversation, messages, scrollToBottom]);

  /** Scroll to latest message when drawer reopens (layout settles after slide-in). */
  useEffect(() => {
    if (!drawerVisible || !hasConversation) return undefined;

    shouldStickToBottomRef.current = true;
    setShowScrollToBottom(false);
    scrollToBottom("auto");

    const afterTransition = window.setTimeout(
      () => scrollToBottom("auto"),
      VERTEX360_COPILOT_DRAWER_TRANSITION_MS + 40
    );

    return () => window.clearTimeout(afterTransition);
  }, [drawerVisible, hasConversation, scrollToBottom]);

  useEffect(() => {
    if (!hasConversation) {
      if (isLoadingMessages) return;

      const el = scrollContainerRef?.current;
      if (el) {
        el.scrollTop = 0;
      }
      lastMessageKeyRef.current = "";
      shouldStickToBottomRef.current = true;
      setShowScrollToBottom(false);
      return;
    }

    const lastMessage = messages[messages.length - 1];
    const messageKey = lastMessage
      ? `${lastMessage.id}:${lastMessage.content?.length || 0}`
      : `typing:${isAssistantTyping}:${isStreaming}`;

    const isNewActivity = messageKey !== lastMessageKeyRef.current;
    lastMessageKeyRef.current = messageKey;

    if (!isNewActivity && !isWaitingForAssistantReply) return;

    if (isNewActivity) {
      shouldStickToBottomRef.current = true;
    }

    if (!shouldStickToBottomRef.current) return;

    setShowScrollToBottom(false);
    scrollToBottom(isNewActivity ? "smooth" : "auto");
  }, [
    hasConversation,
    messages,
    isAssistantTyping,
    isStreaming,
    isWaitingForAssistantReply,
    isLoadingMessages,
    scrollContainerRef,
    scrollToBottom,
  ]);

  useEffect(() => {
    const root = scrollContainerRef?.current;
    const sentinel = topSentinelRef.current;
    if (!root || !sentinel || !hasMoreOlder || isLoadingOlder) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasUserScrolledUpRef.current) {
          onLoadOlder?.();
        }
      },
      { root, rootMargin: "32px 0px 0px 0px", threshold: 0.01 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    hasMoreOlder,
    isLoadingOlder,
    onLoadOlder,
    scrollContainerRef,
    messages.length,
    conversationId,
  ]);

  return (
    <div
      ref={scrollContainerRef}
      data-copilot-chat-scroll
      className="copilot-chat-scroll relative z-[1] flex-1 overflow-y-auto overscroll-y-contain px-4 py-4"
      aria-label="Vertex360 Copilot conversation"
      role="log"
      aria-live="polite"
    >
      {!hasConversation && !isLoadingMessages ? (
        <div className="flex min-h-full flex-col justify-start py-2">
          <EmptyConversation
            onSelectPrompt={onSelectPrompt}
            disabled={isAssistantTyping || isStreaming}
          />
        </div>
      ) : isLoadingMessages ? (
        <Vertex360CopilotChatMessagesSkeleton />
      ) : (
        <>
          <div ref={topSentinelRef} className="h-px w-full shrink-0" aria-hidden="true" />

          {hasMoreOlder ? (
            <div className="mb-4 flex justify-center">
              <button
                type="button"
                onClick={onLoadOlder}
                disabled={isLoadingOlder}
                className="rounded-full border border-slate-200 bg-white/90 px-4 py-1.5 text-xs font-semibold text-slate-600 shadow-sm backdrop-blur-sm transition-all hover:border-[#C7D7F5] hover:text-[#4B6CB7] hover:shadow disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoadingOlder ? "Loading older messages…" : "Load older messages"}
              </button>
            </div>
          ) : null}

          <div className="space-y-2">
            {messages.map((message, index) => {
              const isEmptyPendingAssistant =
                message.role === "assistant" &&
                !message.isError &&
                !String(message.content || "").trim() &&
                ((isStreaming &&
                  String(message.id) === String(streamingMessageId ?? "")) ||
                  (isAssistantTyping && index === messages.length - 1));

              if (isEmptyPendingAssistant) {
                return null;
              }

              const retryPrompt = message.isError
                ? messages
                    .slice(0, index)
                    .reverse()
                    .find((item) => item.role === "user")?.content
                : null;

              // For a clarify prompt, the original question is the preceding user turn.
              const clarifyPrompt =
                message.render?.type === "clarify"
                  ? messages
                      .slice(0, index)
                      .reverse()
                      .find((item) => item.role === "user")?.content || null
                  : null;

              const showDivider = shouldShowTimestamp(messages, index);
              const dividerLabel = showDivider
                ? formatChatTimestamp(message.createdAt)
                : null;

              return (
                <React.Fragment key={message.id}>
                  {showDivider && dividerLabel ? (
                    <DateDivider label={dividerLabel} />
                  ) : null}
                  <ChatMessage
                    message={message}
                    conversationId={conversationId}
                    isStreaming={
                      isStreaming &&
                      String(message.id) === String(streamingMessageId ?? "")
                    }
                    isLastAssistant={
                      index === lastAssistantIndex &&
                      !isStreaming &&
                      !isAssistantTyping
                    }
                    onFollowUp={onSelectPrompt}
                    onRetry={onRetryMessage}
                    retryPrompt={retryPrompt}
                    onClarifyOption={onClarifyOption}
                    clarifyPrompt={clarifyPrompt}
                    onMessageRated={onMessageRated}
                  />
                </React.Fragment>
              );
            })}
            {isWaitingForAssistantReply ? <AgentTypingIndicator /> : null}
          </div>
        </>
      )}

      {showScrollToBottom ? (
        <button
          type="button"
          onClick={() => {
            shouldStickToBottomRef.current = true;
            setShowScrollToBottom(false);
            scrollToBottom("smooth");
          }}
          className="copilot-scroll-bottom-button"
          aria-label="Scroll to latest message"
          title="Scroll to latest message"
        >
          <HiOutlineArrowDown
            className={animateScrollToBottom ? "copilot-scroll-bottom-icon--attention" : ""}
            aria-hidden
          />
        </button>
      ) : null}
    </div>
  );
};

export default ChatMessageList;
